// Cache configuration for different types of data
export const CACHE_CONFIG = {
  // Static data that rarely changes
  STATIC: {
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    cacheTime: 7 * 24 * 60 * 60 * 1000, // 7 days
    retry: 3,
  },

  // Semi-static data (teams, players, championships)
  SEMI_STATIC: {
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    cacheTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: 2,
  },

  // Dynamic data that changes regularly (games, standings)
  DYNAMIC: {
    staleTime: 15 * 60 * 1000, // 15 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
  },

  // Real-time data (live scores, current standings)
  REALTIME: {
    staleTime: 30 * 1000, // 30 seconds
    cacheTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  },

  // User-specific data
  USER: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 1,
  }
};

// Query key factories for consistent cache management
export const queryKeys = {
  // Sports and core data
  sports: () => ['sports'] as const,
  sport: (id: string) => ['sports', id] as const,

  // Teams
  teams: () => ['teams'] as const,
  team: (slug: string) => ['teams', slug] as const,
  teamRoster: (slug: string, year?: number) => ['teams', slug, 'roster', year] as const,
  teamSchedule: (slug: string, year?: number) => ['teams', slug, 'schedule', year] as const,
  teamStats: (slug: string, year?: number) => ['teams', slug, 'stats', year] as const,
  teamSeasons: (slug: string) => ['teams', slug, 'seasons'] as const,
  teamChampionships: (slug: string) => ['teams', slug, 'championships'] as const,

  // Players
  players: () => ['players'] as const,
  player: (slug: string) => ['players', slug] as const,
  playersActive: () => ['players', 'active'] as const,
  playersByPosition: (position?: string) => ['players', 'by-position', position] as const,
  playersRookies: () => ['players', 'rookies'] as const,

  // Coaches
  coaches: () => ['coaches'] as const,
  coach: (slug: string) => ['coaches', slug] as const,
  coachesActive: () => ['coaches', 'active'] as const,
  coachesHead: () => ['coaches', 'head'] as const,

  // Games
  games: () => ['games'] as const,
  game: (id: string) => ['games', id] as const,
  gamesToday: () => ['games', 'today'] as const,
  gamesLive: () => ['games', 'live'] as const,
  gamesRecent: () => ['games', 'recent'] as const,
  gamesUpcoming: () => ['games', 'upcoming'] as const,
  gamesByDateRange: (start: string, end: string) => ['games', 'date-range', start, end] as const,

  // Standings
  standings: () => ['standings'] as const,
  standingsCurrent: () => ['standings', 'current'] as const,
  standingsBySeason: (year: number) => ['standings', 'season', year] as const,
  standingsByDivision: (divisionId?: string, year?: number) => ['standings', 'division', divisionId, year] as const,

  // Championships
  championships: () => ['championships'] as const,
  championship: (slug: string) => ['championships', slug] as const,
  championshipStats: (slug: string) => ['championships', slug, 'stats'] as const,
  championshipGames: (slug: string) => ['championships', slug, 'games'] as const,
  championshipChampions: (slug: string) => ['championships', slug, 'champions'] as const,

  // Champions
  champions: () => ['champions'] as const,
  championsByYear: (year: number) => ['champions', 'year', year] as const,

  // Seasons
  seasons: () => ['seasons'] as const,
  season: (id: string) => ['seasons', id] as const,
};

// Cache invalidation patterns
export const invalidationPatterns = {
  // When a team is updated, invalidate all team-related queries
  team: (teamSlug: string) => [
    queryKeys.team(teamSlug),
    queryKeys.teamRoster(teamSlug),
    queryKeys.teamSchedule(teamSlug),
    queryKeys.teamStats(teamSlug),
    queryKeys.teamSeasons(teamSlug),
    queryKeys.teamChampionships(teamSlug),
  ],

  // When a game is updated, invalidate related queries
  game: (gameId: string) => [
    queryKeys.game(gameId),
    queryKeys.games(),
    queryKeys.gamesToday(),
    queryKeys.gamesLive(),
    queryKeys.gamesRecent(),
    queryKeys.gamesUpcoming(),
    queryKeys.standings(),
    queryKeys.standingsCurrent(),
  ],

  // When standings are updated
  standings: () => [
    queryKeys.standings(),
    queryKeys.standingsCurrent(),
  ],

  // When a player is updated
  player: (playerSlug: string) => [
    queryKeys.player(playerSlug),
    queryKeys.players(),
    queryKeys.playersActive(),
    queryKeys.playersRookies(),
  ],
};

// Prefetch strategies for common navigation patterns
export const prefetchStrategies = {
  // When viewing a team, prefetch related data
  teamDetail: (teamSlug: string) => [
    queryKeys.teamRoster(teamSlug),
    queryKeys.teamSchedule(teamSlug),
    queryKeys.teamStats(teamSlug),
  ],

  // When viewing games, prefetch standings
  gamesList: () => [
    queryKeys.standingsCurrent(),
  ],

  // When viewing a player, prefetch related players
  playerDetail: (position?: string) => [
    queryKeys.playersByPosition(position),
  ],
};

// Background refresh patterns for real-time data
export const backgroundRefresh = {
  // Live games should refresh frequently
  liveGames: {
    refetchInterval: 30 * 1000, // 30 seconds
    refetchIntervalInBackground: true,
  },

  // Current standings during active season
  currentStandings: {
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    refetchIntervalInBackground: false,
  },

  // Today's games
  todaysGames: {
    refetchInterval: 2 * 60 * 1000, // 2 minutes
    refetchIntervalInBackground: false,
  },
};

// Cache size management
export const cacheSize = {
  maxQueries: 100, // Maximum number of queries to keep in cache
  maxMutations: 10, // Maximum number of mutations to keep in cache
  gcTime: 24 * 60 * 60 * 1000, // 24 hours garbage collection time
};

// Error retry configurations
export const retryConfig = {
  // Network errors - retry aggressively
  networkError: {
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  },

  // Server errors - retry less aggressively
  serverError: {
    retry: 1,
    retryDelay: 5000,
  },

  // Authentication errors - don't retry
  authError: {
    retry: false,
  },
};